import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, ShieldCheck } from 'lucide-react';
import { paymentApi } from '../api/paymentApi';
import { PaymentCheckout } from '../components/PaymentCheckout';
import { PaymentLoadingScreen, PaymentStatusScreen } from '../components/PaymentStatusScreen';
import { PaymentSuccess } from '../components/PaymentSuccess';
import {
  DEFAULT_PAYMENT_THEME,
  buildPaymentAmounts,
  buildPaymentContact,
  buildPaymentTheme,
  getPaymentMethodKind,
  getTokenError,
} from '../utils/paymentDisplay';

/**
 * Page publique de paiement d'un contrat (`payment/:token`, sans authentification).
 * Une seule mise en page pour tous les paiements : aux couleurs du garant avec le
 * récapitulatif complet quand l'API renvoie le bloc `upsell`, sinon société du
 * contrat et couleurs Affinicar.
 */
export function PaymentPage() {
  const { token } = useParams<{ token: string }>();

  // Nettoyer les query params ajoutés par YouSign (signature_request_id, signer_id)
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const tokenQuery = useQuery({
    queryKey: ['payment-token', token],
    queryFn: () => paymentApi.resolveToken(token as string),
    enabled: Boolean(token),
    retry: false,
    // Résolu une seule fois : le clientSecret ne doit pas changer sous <Elements>.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const completeMutation = useMutation({
    mutationFn: (paymentToken: string) => paymentApi.completePayment(paymentToken),
  });

  const data = tokenQuery.data;
  const signatureLink = data?.requiresSignature ? data.signatureLink : null;
  const stripeData = data && !data.requiresSignature ? data : null;
  const theme = useMemo(() => (stripeData ? buildPaymentTheme(stripeData) : DEFAULT_PAYMENT_THEME), [stripeData]);

  // Contrat pas encore signé : on renvoie le client sur YouSign
  // (reproduit le guard Symfony V1 `stripe_payment_link`).
  useEffect(() => {
    if (signatureLink) {
      window.location.replace(signatureLink);
    }
  }, [signatureLink]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = theme.name ? `Paiement — ${theme.name}` : 'Paiement sécurisé';
    return () => {
      document.title = previousTitle;
    };
  }, [theme.name]);

  // Le token est peut-être déjà marqué utilisé : un échec du /complete affiche quand même le succès.
  const handlePaymentSuccess = () => {
    if (token) completeMutation.mutate(token);
  };

  if (!token) {
    return (
      <PaymentStatusScreen
        icon={AlertTriangle}
        tone="error"
        title="Lien invalide"
        description={<p className="tw-mb-0">Lien de paiement invalide.</p>}
      />
    );
  }

  if (stripeData && (completeMutation.isSuccess || completeMutation.isError)) {
    const upsell = stripeData.upsell ?? null;
    return (
      <PaymentSuccess
        theme={theme}
        footerBranding={upsell?.branding ?? null}
        methodKind={getPaymentMethodKind(stripeData)}
        amounts={buildPaymentAmounts(stripeData.intentType, stripeData.metadata)}
        warrantyName={upsell?.summary.warrantyName ?? null}
        contact={buildPaymentContact(stripeData)}
      />
    );
  }

  if (tokenQuery.isError) {
    const tokenError = getTokenError(tokenQuery.error);

    if (tokenError.kind === 'expired') {
      return (
        <PaymentStatusScreen
          icon={Clock}
          tone="warning"
          title="Lien expiré"
          description={
            <p className="tw-mb-0">
              Ce lien de paiement a expiré. Veuillez contacter votre professionnel pour obtenir un nouveau lien.
            </p>
          }
        />
      );
    }

    if (tokenError.kind === 'used') {
      return (
        <PaymentStatusScreen
          icon={ShieldCheck}
          tone="info"
          title="Paiement déjà effectué"
          description={
            <p className="tw-mb-0">
              Ce lien de paiement a déjà été utilisé. Si vous avez une question, contactez votre professionnel.
            </p>
          }
        />
      );
    }

    return (
      <PaymentStatusScreen
        icon={AlertTriangle}
        tone="error"
        title="Paiement indisponible"
        description={<p className="tw-mb-0">{tokenError.message}</p>}
      />
    );
  }

  if (signatureLink) {
    return <PaymentLoadingScreen message="Redirection vers la signature de votre contrat..." />;
  }

  if (!stripeData) {
    return <PaymentLoadingScreen message="Chargement du paiement..." />;
  }

  return <PaymentCheckout data={stripeData} theme={theme} onSuccess={handlePaymentSuccess} />;
}
